import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Login from './components/Login';
import { auth } from './lib/auth';

// Import all the components that were previously defined in this file
import DownloadWarningBanner from './components/DownloadWarningBanner';
import DownloadReminderModal from './components/DownloadReminderModal';
import ProgressIndicator from './components/ProgressIndicator';
import ContentQualityModal from './components/ContentQualityModal';
import CustomKeywordInput from './components/CustomKeywordInput';
import TitleSelectionModal from './components/TitleSelectionModal';
import ScriptUnlockGate from './components/ScriptUnlockGate';
import EmailCaptureModal from './components/EmailCaptureModal';
import FacebookShareModal from './components/FacebookShareModal';
import ThumbnailGenerator from './components/ThumbnailGenerator';
import VisitLimitModal from './components/VisitLimitModal';
import ApiKeyModal from './components/ApiKeyModal';

const { 
  FiZap, FiTarget, FiTrendingUp, FiPlay, FiLoader, FiAlertCircle, FiCopy, FiCheck, 
  FiList, FiClock, FiChevronDown, FiChevronRight, FiUser, FiDownload, FiExternalLink,
  FiLock, FiMail, FiShare2, FiBookOpen, FiFileText, FiGlobe, FiLogOut, FiRefreshCw,
  FiX, FiSearch, FiEdit3
} = FiIcons;

// Constants
const CONTENT_STRATEGY_FILENAME = 'iHeroes_Content_Strategy.txt';
const THUMBNAIL_LIST_FILENAME = 'iHeroes_Thumbnails.txt';

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Main app states
  const [url, setUrl] = useState('');
  const [results, setResults] = useState(null);
  const [customScripts, setCustomScripts] = useState([]);
  const [thumbnailResults, setThumbnailResults] = useState(null);

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [detailedProgress, setDetailedProgress] = useState('');
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showContentQualityModal, setShowContentQualityModal] = useState(false);
  const [showDownloadReminder, setShowDownloadReminder] = useState(false);
  const [showTitleSelection, setShowTitleSelection] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState('');
  const [keywordTitles, setKeywordTitles] = useState([]);
  const [customKeywordContext, setCustomKeywordContext] = useState('');
  const [showCustomKeywords, setShowCustomKeywords] = useState(false);
  const [customKeywordStep, setCustomKeywordStep] = useState('input');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [shareCompleted, setShareCompleted] = useState(false);
  const [showVisitLimit, setShowVisitLimit] = useState(false);
  const [visitCount, setVisitCount] = useState(0);
  const [isPremiumUser, setIsPremiumUser] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      if (auth.isAuthenticated()) {
        setIsAuthenticated(true);
        setCurrentUser(auth.getCurrentUser());
      }
    };
    checkAuth();
  }, []);

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    auth.signOut();
    setIsAuthenticated(false);
    setCurrentUser(null);
    
    // Reset all other states
    setUrl('');
    setResults(null);
    setCustomScripts([]);
    setThumbnailResults(null);
  };

  // If not authenticated, show login screen
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      {/* Logout Button */}
      <div className="absolute top-4 right-4 z-50">
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-lg rounded-lg text-white hover:bg-white/20 transition-colors border border-white/20"
        >
          <SafeIcon icon={FiLogOut} />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <SafeIcon icon={FiZap} className="text-5xl text-yellow-400 mr-4" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                iHeroes
              </h1>
            </div>
            
            <p className="text-xl text-blue-200 mb-8 max-w-2xl mx-auto">
              Transform any webpage into viral YouTube content with AI-powered scripts, keywords, and thumbnails
            </p>

            {/* User status indicator */}
            {currentUser && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6 flex items-center justify-center gap-2 text-green-300"
              >
                <SafeIcon icon={FiUser} />
                <span>Welcome, {currentUser.name}</span>
                <span className="bg-green-500/20 text-green-300 px-2 py-0.5 text-xs rounded-full">
                  Authenticated User
                </span>
              </motion.div>
            )}
          </motion.div>

          {/* Main Content Area */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">
                Generate Viral YouTube Content
              </h2>
              <p className="text-blue-200">
                Enter any webpage URL to create 10 complete video scripts with SEO keywords and thumbnails
              </p>
            </div>

            {/* URL Input Form */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com/product-page"
                    className="w-full px-6 py-4 bg-white/20 text-white placeholder-blue-200 border border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-yellow-400"
                    disabled={isLoading}
                  />
                </div>
                <motion.button
                  whileHover={{ scale: isLoading ? 1 : 1.05 }}
                  whileTap={{ scale: isLoading ? 1 : 0.95 }}
                  onClick={() => {}}
                  disabled={isLoading || !url}
                  className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl hover:from-yellow-300 hover:to-orange-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <SafeIcon icon={FiLoader} className="animate-spin" />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiZap} />
                      <span>Generate Content</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 p-6 rounded-xl border border-white/20">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiTarget} className="text-yellow-400 text-2xl" />
                  <h3 className="text-lg font-bold text-white">10 Problems</h3>
                </div>
                <p className="text-blue-200">
                  Extract customer pain points from any webpage
                </p>
              </div>
              
              <div className="bg-white/10 p-6 rounded-xl border border-white/20">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiTrendingUp} className="text-green-400 text-2xl" />
                  <h3 className="text-lg font-bold text-white">50 Keywords</h3>
                </div>
                <p className="text-blue-200">
                  Generate high-ROI SEO keywords for each problem
                </p>
              </div>
              
              <div className="bg-white/10 p-6 rounded-xl border border-white/20">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiPlay} className="text-blue-400 text-2xl" />
                  <h3 className="text-lg font-bold text-white">10 Scripts</h3>
                </div>
                <p className="text-blue-200">
                  Create complete 3-minute YouTube scripts with director's notes
                </p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-8 text-center text-blue-200">
            <p>🔒 This is a private application. Your data is secure and never shared.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;